import {
  addMonths,
  differenceInCalendarDays,
  eachDayOfInterval,
  endOfMonth,
  endOfWeek,
  format,
  isSameMonth,
  parseISO,
  startOfWeek,
} from 'date-fns';

export type CalendarDay = { date: string; inMonth: boolean };

/**
 * Sunday-first weeks covering `month` ("YYYY-MM"), padded with adjacent-month
 * days. As many rows as the month needs (Jul 2026 has 5), never a fixed 6
 * (UI spec §7).
 */
export const monthGrid = (month: string): CalendarDay[][] => {
  const first = parseISO(`${month}-01`);
  const days = eachDayOfInterval({
    start: startOfWeek(first, { weekStartsOn: 0 }),
    end: endOfWeek(endOfMonth(first), { weekStartsOn: 0 }),
  });
  const weeks: CalendarDay[][] = [];
  for (let i = 0; i < days.length; i += 7) {
    weeks.push(
      days.slice(i, i + 7).map((d) => ({
        date: format(d, 'yyyy-MM-dd'),
        inMonth: isSameMonth(d, first),
      })),
    );
  }
  return weeks;
};

/** The dates the grid shows: what the calendar asks graphy for. */
export const gridRange = (month: string): { from: string; to: string } => {
  const weeks = monthGrid(month);
  return { from: weeks[0][0].date, to: weeks[weeks.length - 1][6].date };
};

export const shiftMonth = (month: string, delta: number): string =>
  format(addMonths(parseISO(`${month}-01`), delta), 'yyyy-MM');

export type Selection = { start: string; end: string | null };

/** graphy's cap on one save's dates (OpsRota::Inputs::MAX_DATES). */
export const MAX_RANGE_DATES = 62;

/**
 * The prototype's click rule (UI spec §7.1 `pickRange`): with a single date
 * selected, a later date makes a contiguous range; anything else (nothing
 * selected, a range already there, the same or an earlier date) becomes the
 * new single date. R6 adds one rule: a range never starts in the past, so a
 * click after a past single date selects that date on its own. So
 * does a click that would make a range of more than MAX_RANGE_DATES dates,
 * which graphy would refuse only after Save.
 *
 * `opensDay` lets a later date open on its own instead of ending a
 * range. The Operations page passes "has schedules", so a click on a day
 * with blocks shows them (OP-13) while an empty later day still starts a
 * range. The Repeat modal passes nothing: there every click picks targets.
 */
export const pickRange = (
  current: Selection | null,
  clicked: string,
  today: string | null,
  opensDay: (date: string) => boolean = () => false,
): Selection => {
  const startsInPast =
    current !== null && today !== null && current.start < today;
  const tooLong =
    current !== null &&
    differenceInCalendarDays(parseISO(clicked), parseISO(current.start)) + 1 >
      MAX_RANGE_DATES;
  if (
    current === null ||
    current.end !== null ||
    clicked <= current.start ||
    startsInPast ||
    tooLong ||
    opensDay(clicked)
  ) {
    return { start: clicked, end: null };
  }
  return { start: current.start, end: clicked };
};

/** Where `date` sits in `selection`, for the calendar's styling. */
export type DayRole = 'single' | 'start' | 'end' | 'middle' | null;

export const dayRole = (selection: Selection | null, date: string): DayRole => {
  if (!selection) return null;
  const { start, end } = selection;
  if (end === null) return date === start ? 'single' : null;
  if (date === start) return 'start';
  if (date === end) return 'end';
  return date > start && date < end ? 'middle' : null;
};
